'use client'

import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import Image from 'next/image'

export default function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { signIn } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) return

    setLoading(true)
    setError('')

    try {
      const { error } = await signIn(email, password)
      
      if (error) {
        setError(error.message)
      }
    } catch (error: any) {
      setError(error.message || 'An error occurred during login')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-between w-[344px]">
      {/* Brand Logo */}
      <Image
        src="/zulu-brand.svg"
        alt="zulu-brand"
        width={120}
        height={56}
        className="object-cover"
      />

      {/* Title and Description */}
      <div className="flex flex-col items-center justify-between my-12 gap-y-2">
        <h2 className="title-28-bold text-tx-default">Welcome back</h2>
        <p className="body-16-regular text-tx-secondary">Log in to your account</p>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="w-full">
        <div className="flex flex-col w-full gap-y-4">
          {/* Email Input */}
          <div className="flex flex-col gap-2">
            <label htmlFor="email" className="text-sm font-medium text-tx-default">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
              required
              autoFocus
            />
          </div>

          {/* Password Input */}
          <div className="flex flex-col gap-2">
            <label htmlFor="password" className="text-sm font-medium text-tx-default">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
              required
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !email || !password}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              loading || !email || !password
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Signing in...
              </div>
            ) : (
              'Sign in'
            )}
          </button>
        </div>
      </form>

      {/* Contact Us */}
      <div className="flex flex-col items-center justify-between gap-y-2 mt-12">
        <p className="body-16-regular text-tx-secondary">Need help?</p>
        <a
          href="https://www.zulu.com.co/contactanos"
          className="body-16-regular text-tx-brand"
          target="_blank"
          rel="noopener noreferrer"
        >
          Contact us
        </a>
      </div>
    </div>
  )
}
